
import { z } from "zod";
import { Recruitment } from "../types/Recruitment";

export const RegionTypeSchema = z.enum(['Midlands', 'West', 'Scotland', 'London', 'Leeds', 'Manchester'])

export const WorkStatusTypeSchema = z.enum(['Unemployed', 'Employed', 'Registered self employed/Self Assessment', 'Limited Company (LTD)', 'International Student'])

export const YesNoTypeSchema = z.enum(['Yes', 'No'])

export const AvailabilityTypeSchema = z.enum(['Less than 25 hours per week', 'More than 25 hours per week'])

export enum ApplicationStatus {
  Draft = 0,
  Submitted = 1,
  Accepted = 2,
  Rejected = 3,
}

export const ApplicationStatusText: { [key in ApplicationStatus]: string } = {
  [ApplicationStatus.Draft]: 'Draft',
  [ApplicationStatus.Submitted]: 'Submitted',
  [ApplicationStatus.Accepted]: 'Accepted',
  [ApplicationStatus.Rejected]: 'Rejected',
};


export const yourDetailsOnboardingFormSchema = z.object({
  first_name: z.string().nullish(),
  last_name: z.string().nullish(),
  region: z.string().nullish(),
  address_line_1: z.string().nullish(),
  address_line_2: z.string().nullish(),
  address_city: z.string().nullish(),
  address_county: z.string().nullish(),
  address_postcode: z.string().nullish(),
  address_country: z.string().nullish(),
  phone: z.string().nullish(),
  work_status: z.string().nullish(),
  work_permit: z.string().nullish(),
  current_availability: z.string().nullish(),
  criminal_convictions: z.string().nullish(),
  disability: z.string().nullish(),
  immigration_share_code: z.string().nullish(),
});

export const mapYourDetailsOnboardingFormForToFrontend = (data: Recruitment) => ({
  first_name: data?.first_name || '',
  last_name: data?.last_name || '',
  region: data?.region,
  address_line_1: data?.address_line_1 || '',
  address_line_2: data?.address_line_2 || '',
  address_city: data?.address_city || '',
  address_county: data?.address_county || '',
  address_postcode: data?.address_postcode || '',
  address_country: data?.address_country || '',
  phone: data?.phone || '',
  work_status: data?.work_status || null,
  work_permit: convertToYesNo(data?.work_permit),
  current_availability: data?.current_availability || '',
  immigration_share_code: data?.immigration_share_code || '',
});

export const mapYourDetailsOnboardingFormForBackend = (id: number, data: z.infer<typeof yourDetailsOnboardingFormSchema>) => ({
  id,
  first_name: data.first_name,
  last_name: data.last_name,
  region: data.region,
  address_line_1: data.address_line_1,
  address_line_2: data.address_line_2,
  address_city: data.address_city,
  address_county: data.address_county,
  address_postcode: data.address_postcode,
  address_country: data.address_country,
  phone: data.phone,
  work_status: data.work_status,
  work_permit: data.work_permit === 'Yes',
  current_availability: data.current_availability,
  criminal_convictions: data.criminal_convictions === 'Yes',
  disability: data.disability === 'Yes',
  immigration_share_code: data.immigration_share_code,
});

export const yourExperienceOnboardingFormSchema = z.object({
  driving_license: z.string().nullish(),
  driving_license_endorsements: z.string().nullish(),
  vehicle_owner: z.string().nullish(),
  specialist_license: z.string().nullish(),
  specialist_license_type: z.string().nullish(),
  lead_details: z.string().nullish(),
  crew_services: z.string().nullish(),
  crew_services_details: z.string().nullish(),
  skills_description: z.string().nullish(),
  team_description: z.string().nullish(),
});

export const mapExperienceOnboardingFormForToFrontend = (data: Recruitment) => ({
  driving_license: convertToYesNo(data?.driving_license),
  driving_license_endorsements: data?.driving_license_endorsements || '',
  vehicle_owner: convertToYesNo(data?.vehicle_owner),
  specialist_license: convertToYesNo(data?.specialist_license),
  specialist_license_type: data?.specialist_license_type || '',
  crew_services: convertToYesNo(data?.crew_services),
  crew_services_details: data?.crew_services_details || '',
  skills_description: data?.skills_description || '',
  team_description: data?.team_description || '',
  lead_details: data?.lead_details || '',
});

export const mapYourExperienceOnboardingFormForBackend = (id: number, data: z.infer<typeof yourExperienceOnboardingFormSchema>) => ({
  id,
  driving_license: data.driving_license === 'Yes',
  driving_license_endorsements: data.driving_license_endorsements,
  vehicle_owner: data.vehicle_owner === 'Yes',
  specialist_license: data.specialist_license === 'Yes',
  specialist_license_type: data.specialist_license_type,
  lead_details: data.lead_details,
  crew_services: data.crew_services === 'Yes',
  crew_services_details: data.crew_services_details,
  skills_description: data.skills_description,
  team_description: data.team_description,
});


export const competencyQuestionsOnboardingFormSchema = z.object({
  tasks_assigned_description: z.string().nullish(),
  completed_task_description: z.string().nullish(),
  dangerous_description: z.string().nullish(),
});

export const mapCompetencyQuestionsOnboardingFormForToFrontend = (data: Recruitment) => ({
  tasks_assigned_description: data?.tasks_assigned_description || '',
  completed_task_description: data?.completed_task_description || '',
  dangerous_description: data?.dangerous_description || '',
});

export const mapCompetencyQuestionsOnboardingFormForBackend = (id: number, data: z.infer<typeof competencyQuestionsOnboardingFormSchema>) => ({
  id,
  tasks_assigned_description: data.tasks_assigned_description,
  completed_task_description: data.completed_task_description,
  dangerous_description: data.dangerous_description,
});

export const refereeOnboardingFormSchema = z.object({
  referee_name: z.string().nullish(),
  referee_phone: z.string().nullish(),
  marketing: z.boolean().nullish(),
  t_c_agreement: z.boolean().nullish(),
});

export const mapRefereeOnboardingFormForToFrontend = (data: Recruitment) => ({
  referee_name: data?.referee_name || '',
  referee_phone: data?.referee_phone || '',
  marketing: data?.marketing || null,
  t_c_agreement: data?.t_c_agreement || null,
});

export const mapRefereeOnboardingFormForBackend = (id: number, data: z.infer<typeof refereeOnboardingFormSchema>) => ({
  id,
  referee_name: data.referee_name,
  referee_phone: data.referee_phone,
  marketing: data.marketing,
  t_c_agreement: data.t_c_agreement,
});


export const mandatorySubmitYourDetailsFields: Array<keyof Recruitment> = [
  'first_name',
  'last_name',
  'region',
  'address_line_1',
  'address_city',
  'address_county',
  'address_postcode',
  'address_country',
  'phone',
  'work_status',
  'current_availability',
];

export const isAllYourDetailsFieldsComplete = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;

  const missingFields = mandatorySubmitYourDetailsFields.filter(field =>
    recruitmentData[field] === null || recruitmentData[field] === undefined || recruitmentData[field] === ''
  );

  if (missingFields.length > 0) {
    return false;
  }

  return true;
}

export const isYourDetailsFieldsStarted = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitYourDetailsFields.some(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

const mandatorySubmitYourExperienceFields: Array<keyof Recruitment> = [
  'driving_license',
  'vehicle_owner',
  'specialist_license',
  'crew_services',
  'skills_description',
  'team_description',
];

export const isAllYourExperienceFieldsComplete = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitYourExperienceFields.every(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

export const isYourExperienceFieldsStarted = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitYourExperienceFields.some(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

const mandatorySubmitFieldsCompetencyQuestions: Array<keyof Recruitment> = [
  'tasks_assigned_description',
  'completed_task_description',
  'dangerous_description',
];

export const isAllCompetencyQuestionsFieldsComplete = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitFieldsCompetencyQuestions.every(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

export const isCompetencyQuestionsFieldsStarted = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitFieldsCompetencyQuestions.some(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

const mandatorySubmitFieldsReferee: Array<keyof Recruitment> = [
  'referee_name',
  'referee_phone',
  't_c_agreement',
];

export const isAllRefereeFieldsComplete = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitFieldsReferee.every(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

export const isRefereeFieldsStarted = (recruitmentData: Recruitment | null | undefined) => {
  if (!recruitmentData) return false;
  return mandatorySubmitFieldsReferee.some(field =>
    recruitmentData[field] !== null && recruitmentData[field] !== undefined && recruitmentData[field] !== ''
  );
}

const convertToYesNo = (value: boolean | null): string | null => {
  if (value === null) return null;
  return value ? 'Yes' : 'No';
};